import React, { useState, useMemo, useRef, useCallback } from "react";
import { BsArrowDownUp } from "react-icons/bs";
import Draggable from "react-draggable";
import "./payments.css";

const PaymentsTable = ({
  currentPosts = [],
  handleCheckboxChange,
  selectedIds = [],
  selectAll = false,
  handleSelectAll,
}) => {
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [selectedPayment, setSelectedPayment] = useState(null);

  const [columnWidths, setColumnWidths] = useState({
    id: 40,
    postDate: 80,
    particulars: 160,
    account_no: 120,
    amount: 80,
    account_type: 100,
    name: 100,
  });

  const resizingCol = useRef(null);
  const startX = useRef(0);
  const startWidth = useRef(0);

  // Format date as YYYY-MM-DD
  const formatPostDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (isNaN(date)) return value;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Format number as INR
  const formatNumber = (value) => {
    if (value == null || value === "") return "";
    const num = parseFloat(value);
    if (isNaN(num)) return String(value);
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(num);
  };

  // Remove duplicate payments based on unique `id`
  const uniquePosts = useMemo(() => {
    const seen = new Set();
    return currentPosts.filter((item) => {
      if (!item?.id) return false;
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  }, [currentPosts]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!Array.isArray(uniquePosts) || uniquePosts.length === 0) return [];

    const arr = [...uniquePosts];
    if (sortColumn) {
      arr.sort((a, b) => {
        let vA = a[sortColumn];
        let vB = b[sortColumn];

        if (vA == null) return 1;
        if (vB == null) return -1;

        if (sortColumn === "postDate") {
          vA = new Date(vA).getTime();
          vB = new Date(vB).getTime();
        }

        return sortDirection === "asc"
          ? vA < vB ? -1 : vA > vB ? 1 : 0
          : vA > vB ? -1 : vA < vB ? 1 : 0;
      });
    }

    return arr;
  }, [uniquePosts, sortColumn, sortDirection]);

  // Handle column sorting
  const handleSort = useCallback((col) => {
    setSortColumn((prev) => (prev === col ? col : col));
    setSortDirection((prev) => (sortColumn === col && prev === "asc" ? "desc" : "asc"));
  }, [sortColumn]);

  // Resizing columns
  const onMouseDown = useCallback((e, colKey) => {
    resizingCol.current = colKey;
    startX.current = e.clientX;
    startWidth.current = columnWidths[colKey];
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }, [columnWidths]);

  const onMouseMove = useCallback((e) => {
    if (!resizingCol.current) return;
    const dx = e.clientX - startX.current;
    setColumnWidths((prev) => {
      const newWidth = Math.max(startWidth.current + dx, 50);
      return { ...prev, [resizingCol.current]: newWidth };
    });
  }, []);

  const onMouseUp = useCallback(() => {
    resizingCol.current = null;
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
  }, [onMouseMove]);

  const columns = [
    { key: "id", label: "ID", align: "center", bold: true },
    { key: "postDate", label: "Post Date", format: formatPostDate },
    { key: "particulars", label: "Particulars" },
    { key: "amount", label: "Amount", format: formatNumber },
    { key: "account_type", label: "Account Type" },
    { key: "name", label: "Name" },
    { key: "account_no", label: "Account Number" },
  ];

  return (
    <div className="table-responsive">
      <table className="table table-bordered">
        <thead>
          <tr>
            <th style={{ width: 30, minWidth: 30 }} className="text-center">
              <input
                type="checkbox"
                checked={
                  uniquePosts.length > 0 &&
                  uniquePosts.every((item) => selectedIds.includes(item.id))
                }
                onChange={handleSelectAll}
              />
            </th>
            {columns.map((col) => (
              <th
                key={col.key}
                className={`text-${col.align || "left"} sortable`}
                style={{
                  width: columnWidths[col.key],
                  minWidth: 50,
                  position: "relative",
                  userSelect: "none",
                  cursor: "pointer",
                }}
                onClick={() => handleSort(col.key)}
              >
                <div className="d-flex align-items-center justify-content-center gap-1">
                  <BsArrowDownUp size={12} />
                  <span className={col.bold ? "fw-bold" : ""}>{col.label}</span>
                </div>
                <div
                  className="resize-handle"
                  onMouseDown={(e) => onMouseDown(e, col.key)}
                  style={{
                    position: "absolute",
                    right: 0,
                    top: 0,
                    bottom: 0,
                    width: "5px",
                    cursor: "col-resize",
                    userSelect: "none",
                  }}
                />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row) => (
            <tr
              key={row.id}
              style={{
                backgroundColor: selectedIds.includes(row.id) ? "#c1d3f0" : "",
              }}
            >
              <td className="text-center">
                <input
                  type="checkbox"
                  checked={selectedIds.includes(row.id)}
                  onChange={() => handleCheckboxChange(row.id)}
                />
              </td>
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={`wrap-text-style text-${col.align || "left"} ${
                    col.bold ? "fw-bold" : ""
                  }`}
                  style={{
                    width: columnWidths[col.key],
                    minWidth: 50,
                  }}
                >
                  {col.key === "particulars" ? (
                    <span
                      className="particulars-link"
                      onClick={() => setSelectedPayment(row)}
                      title="View Payment Details"
                      style={{
                        cursor: "pointer",
                        color: "rgb(0, 123, 255)",
                        textDecoration: "underline",
                      }}
                    >
                      {row[col.key]}
                    </span>
                  ) : col.format ? (
                    col.format(row[col.key])
                  ) : (
                    row[col.key]
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {selectedPayment && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1200 }}
        >
          <div className="modal-dialog modal-lg">
            <Draggable handle=".modal-header">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    Payment - {selectedPayment.particulars}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setSelectedPayment(null)}
                    aria-label="Close"
                  />
                </div>
                <div
                  className="modal-body"
                  style={{ maxHeight: "400px", overflowY: "auto" }}
                >
                  <ul className="list-group">
                    {Object.entries(selectedPayment).map(([key, value]) => (
                      <li
                        key={key}
                        className="list-group-item d-flex justify-content-between"
                        style={{
                          wordBreak: "break-word",
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        <strong>{key}</strong>
                        <span style={{ fontFamily: "monospace" }}>
                          {String(value)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setSelectedPayment(null)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </Draggable>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentsTable;
